<script setup lang="ts">
import { Icon } from '@iconify/vue';
import { onMounted, onUnmounted } from 'vue';
import { IconLib } from '@/assets/icon-library.ts';
import WhatsNewContent from '@/components/doc-ai/doc-search/whats-new/whats-new-content.vue';

const modelValue = defineModel<boolean>('modelValue');

function hide() {
   modelValue.value = false;
   document.body.style.overflow = '';
}

function handleBackdropClick(e: MouseEvent) {
   if (e.target === e.currentTarget) {
      hide();
   }
}

function handleEscape(e: KeyboardEvent) {
   if (e.key === 'Escape' && modelValue.value) {
      hide();
   }
}

onMounted(() => {
   document.addEventListener('keydown', handleEscape);
});

onUnmounted(() => {
   document.removeEventListener('keydown', handleEscape);
});
</script>

<template>
   <Teleport to="body">
      <Transition
         enter-active-class="duration-300 ease-in"
         enter-from-class="opacity-0"
         enter-to-class="opacity-100"
         leave-active-class="duration-200 ease-in"
         leave-from-class="opacity-100"
         leave-to-class="opacity-0"
      >
         <div
            v-if="modelValue"
            class="fixed inset-0 z-[9999] p-4"
         >
            <!-- Backdrop -->
            <div class="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm" @click="handleBackdropClick" />

            <!-- Modal -->
            <div class="flex min-h-full items-center justify-center">
               <Transition
                  enter-active-class="duration-300 ease-out"
                  enter-from-class="opacity-0 scale-95"
                  enter-to-class="opacity-100 scale-100"
                  leave-active-class="duration-200 ease-in"
                  leave-from-class="opacity-100 scale-100"
                  leave-to-class="opacity-0 scale-95"
               >
                  <!-- Gradient border wrapper -->
                  <div
                     v-if="modelValue"
                     class="relative w-full max-w-4xl p-[3px] bg-gradient-to-r from-primary to-[#3DB771] rounded-2xl"
                  >
                     <!-- Modal content with white background -->
                     <div class="w-full h-full rounded-2xl bg-white overflow-hidden shadow-xl">
                        <!-- Header -->
                        <div class="sticky top-0 z-10 flex items-center justify-between px-6 lg:px-8 py-4 bg-white">
                           <h2 class="text-xl lg:text-2xl font-medium text-black">
                              What's New in Luma
                           </h2>

                           <!-- Close button -->
                           <button
                              class="size-10 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition-colors"
                              @click="hide"
                           >
                              <Icon :icon="IconLib.actions.close" class="size-5" />
                           </button>
                        </div>

                        <!-- Content -->
                        <div class="max-h-[calc(100vh-10rem)] overflow-y-auto px-6 lg:px-8 pb-6">
                           <WhatsNewContent variant="popup" />
                        </div>
                     </div>
                  </div>
               </Transition>
            </div>
         </div>
      </Transition>
   </Teleport>
</template>

<style scoped>
.duration-200 {
   transition-duration: 200ms;
}

.duration-300 {
   transition-duration: 300ms;
}

.ease-in {
   transition-timing-function: cubic-bezier(0.4, 0, 1, 1);
}

.ease-out {
   transition-timing-function: cubic-bezier(0, 0, 0.2, 1);
}
</style>
